import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { RefreshCw, ExternalLink } from "lucide-react";
import { useAutoSave } from "@/hooks/useAutoSave";
import { AutoSaveIndicator } from "@/components/admin/AutoSaveIndicator";

const EventSettings = ({ embedded = false }: { embedded?: boolean }) => {
  const { organization } = useOrganization();
  const [eventName, setEventName] = useState("");
  const [eventLocation, setEventLocation] = useState("");
  const [eventStartDate, setEventStartDate] = useState("");
  const [eventEndDate, setEventEndDate] = useState("");
  const [eventDescription, setEventDescription] = useState("");
  const [eventCause, setEventCause] = useState("");
  const [eventbriteUrl, setEventbriteUrl] = useState("");
  const [lumaUrl, setLumaUrl] = useState("");
  const [registrationDeadline, setRegistrationDeadline] = useState("");
  const [fetching, setFetching] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Use ref to access latest values in save callback
  const valuesRef = useRef({
    eventName, eventLocation, eventStartDate, eventEndDate, 
    eventDescription, eventCause, eventbriteUrl, lumaUrl, registrationDeadline
  });
  
  // Keep ref updated
  useEffect(() => {
    valuesRef.current = {
      eventName, eventLocation, eventStartDate, eventEndDate,
      eventDescription, eventCause, eventbriteUrl, lumaUrl, registrationDeadline
    };
  }, [eventName, eventLocation, eventStartDate, eventEndDate, eventDescription, eventCause, eventbriteUrl, lumaUrl, registrationDeadline]);

  // Auto-save functionality - batch all updates in a single call
  const saveSettings = useCallback(async () => {
    if (!organization?.id) {
      throw new Error("Organization not loaded");
    }
    
    const v = valuesRef.current;
    console.log("[EventSettings] saveSettings called with:", v);
    
    const updates = [
      { key: 'EVENT_NAME', value: v.eventName },
      { key: 'EVENT_LOCATION', value: v.eventLocation },
      { key: 'EVENT_START_DATE', value: v.eventStartDate },
      { key: 'EVENT_END_DATE', value: v.eventEndDate },
      { key: 'EVENT_DESCRIPTION', value: v.eventDescription },
      { key: 'EVENT_CAUSE', value: v.eventCause },
      { key: 'EVENTBRITE_URL', value: v.eventbriteUrl },
      { key: 'LUMA_URL', value: v.lumaUrl },
      { key: 'REGISTRATION_DEADLINE', value: v.registrationDeadline },
      { key: 'NEXT_EVENT_DATE', value: v.eventStartDate ? new Date(v.eventStartDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '' },
      { key: 'NEXT_EVENT_CAUSE', value: v.eventCause },
    ];

    console.log("[EventSettings] Starting saves for keys:", updates.map(u => u.key));
    
    // Use check-then-save pattern to handle composite unique constraint (key, organization_id)
    await Promise.all(
      updates.map(async (update) => {
        const { data: existing } = await supabase
          .from("settings")
          .select("id")
          .eq("key", update.key)
          .eq("organization_id", organization.id)
          .maybeSingle();

        if (existing) {
          const { error } = await supabase
            .from("settings")
            .update({ value: update.value, updated_at: new Date().toISOString() })
            .eq("key", update.key)
            .eq("organization_id", organization.id);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("settings")
            .insert({ 
              key: update.key, 
              value: update.value, 
              organization_id: organization.id 
            });
          if (error) throw error;
        }
      })
    );
    
    console.log("[EventSettings] All settings saved successfully");
  }, [organization?.id]);

  const { status: saveStatus, markDirty, markClean } = useAutoSave({
    onSave: saveSettings,
    debounceMs: 400,
    enabled: isAdmin,
  });

  // Extract Eventbrite Event ID from URL
  const extractEventbriteId = (url: string): string | null => {
    if (!url) return null;
    const match = url.match(/\/e\/[^\/]*?-?(\d{10,})/);
    return match ? match[1] : null;
  };

  const eventbriteEventId = extractEventbriteId(eventbriteUrl);

  useEffect(() => {
    checkAdminAndFetchSettings();
  }, []);

  const checkAdminAndFetchSettings = async () => {
    setFetching(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("Not authenticated");
        return;
      }

      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();

      if (!roles) {
        setIsAdmin(false);
        return;
      }

      setIsAdmin(true);
      await fetchSettings();
    } catch (error: any) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setFetching(false);
    }
  };

  const fetchSettings = async () => {
    if (!organization?.id) return;
    
    try {
      const { data } = await supabase
        .from("settings")
        .select("key, value")
        .eq("organization_id", organization.id)
        .in("key", [
          "EVENT_NAME", 
          "EVENT_LOCATION", 
          "EVENT_START_DATE", 
          "EVENT_END_DATE", 
          "EVENT_DESCRIPTION",
          "EVENT_CAUSE",
          "EVENTBRITE_URL",
          "LUMA_URL",
          "REGISTRATION_DEADLINE"
        ]);

      if (data) {
        const settings: Record<string, string> = {};
        data.forEach(item => settings[item.key] = item.value);

        setEventName(settings.EVENT_NAME || "");
        setEventLocation(settings.EVENT_LOCATION || "");
        setEventStartDate(settings.EVENT_START_DATE || "");
        setEventEndDate(settings.EVENT_END_DATE || "");
        setEventDescription(settings.EVENT_DESCRIPTION || "");
        setEventCause(settings.EVENT_CAUSE || "");
        setEventbriteUrl(settings.EVENTBRITE_URL || "");
        setLumaUrl(settings.LUMA_URL || "");
        setRegistrationDeadline(settings.REGISTRATION_DEADLINE || "");
        
        // Mark settings as loaded immediately (prevents initial save trigger)
        markClean();
      }
    } catch (error: any) {
      toast.error(`Failed to fetch settings: ${error.message}`);
    }
  };

  if (fetching) {
    return (
      <div className={embedded ? "" : "p-8"}>
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className={embedded ? "" : "p-8"}>
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">
            You need admin privileges to access event settings.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className={embedded ? "pt-0" : "p-8"}>
      <div className="max-w-3xl">
        {!embedded && (
          <>
            <h1 className="text-2xl font-bold font-display mb-2">Event Settings</h1>
            <p className="text-muted-foreground mb-6">
              Configure event details for structured data and schema markup
            </p>
          </>
        )}

        <div className="space-y-6">
          {/* Event Information */}
          <Card className="p-6 space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Event Information</h3>
              <p className="text-sm text-muted-foreground mb-4">
                These details will be included in the Event schema JSON-LD for search engines
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="event-name">Event Name *</Label>
                  <Input
                    id="event-name"
                    value={eventName}
                    onChange={(e) => { setEventName(e.target.value); markDirty(); }}
                    placeholder="BuildWithChrist.ai - AI Workshop for Catholic Event Organizers"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="event-location">Location</Label>
                  <Input
                    id="event-location"
                    value={eventLocation}
                    onChange={(e) => { setEventLocation(e.target.value); markDirty(); }}
                    placeholder="Virtual Event"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    For virtual events, use "Virtual Event" or "Online"
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="event-start">Start Date & Time *</Label>
                    <Input
                      id="event-start"
                      type="datetime-local"
                      value={eventStartDate}
                      onChange={(e) => { setEventStartDate(e.target.value); markDirty(); }}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="event-end">End Date & Time *</Label>
                    <Input
                      id="event-end"
                      type="datetime-local"
                      value={eventEndDate}
                      onChange={(e) => { setEventEndDate(e.target.value); markDirty(); }}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="registration-deadline">Registration Deadline</Label>
                  <Input
                    id="registration-deadline"
                    type="date"
                    value={registrationDeadline}
                    onChange={(e) => { setRegistrationDeadline(e.target.value); markDirty(); }}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Used for "Register by [date]" urgency messaging across the site
                  </p>
                </div>

                <div>
                  <Label htmlFor="event-cause">Cause / Focus Area *</Label>
                  <Input
                    id="event-cause"
                    value={eventCause}
                    onChange={(e) => { setEventCause(e.target.value); markDirty(); }}
                    placeholder="e.g., A Local Georgia Partner"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    The cause or mission focus for this event (displayed on homepage)
                  </p>
                </div>

                <div>
                  <Label htmlFor="event-description">Event Description *</Label>
                  <Textarea
                    id="event-description"
                    value={eventDescription}
                    onChange={(e) => { setEventDescription(e.target.value); markDirty(); }}
                    placeholder="Learn to use AI tools for Catholic events with hands-on exercises, resource library, and community support."
                    rows={4}
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    A brief description of what participants will learn or experience
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Registration Platforms */}
          <Card className="p-6 space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Registration Platforms</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Configure registration URLs for this event
              </p>
              
              <div className="space-y-4">
                {/* Eventbrite */}
                <div>
                  <Label htmlFor="eventbrite-url">Eventbrite Event URL</Label>
                  <Input
                    id="eventbrite-url"
                    type="url"
                    value={eventbriteUrl}
                    onChange={(e) => { setEventbriteUrl(e.target.value); markDirty(); }}
                    placeholder="https://www.eventbrite.com/e/your-event-123456789"
                  />
                  <div className="flex items-center gap-4 mt-2">
                    {eventbriteEventId && (
                      <p className="text-xs text-muted-foreground">
                        <span className="font-medium">Event ID:</span> {eventbriteEventId}
                      </p>
                    )}
                    {eventbriteUrl && (
                      <a 
                        href={eventbriteUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-primary flex items-center gap-1 hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Open in Eventbrite
                      </a>
                    )}
                  </div>
                </div>

                {/* Luma */}
                <div>
                  <Label htmlFor="luma-url">Luma Event URL</Label>
                  <Input
                    id="luma-url"
                    type="url"
                    value={lumaUrl}
                    onChange={(e) => { setLumaUrl(e.target.value); markDirty(); }}
                    placeholder="https://lu.ma/your-event"
                  />
                  <div className="flex items-center gap-4 mt-2">
                    {lumaUrl && (
                      <a 
                        href={lumaUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-primary flex items-center gap-1 hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Open in Luma
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <div className="flex items-center justify-between">
            <AutoSaveIndicator status={saveStatus} />
            <p className="text-xs text-muted-foreground">Changes are saved automatically</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventSettings;
